// Branded confirmation dialog. Drop-in replacement for
// ``window.confirm`` so prompts match the rest of the app instead
// of falling back to the OS-styled browser modal.
//
// Usage:
//   const [open, setOpen] = useState(false);
//   ...
//   <ConfirmDialog
//     open={open}
//     title="Unlink file?"
//     body="The file will remain in Account Documents."
//     confirmLabel="Unlink"
//     variant="danger"
//     onConfirm={() => { doIt(); setOpen(false); }}
//     onCancel={() => setOpen(false)}
//   />

import { useEffect, useRef } from "react";


export type ConfirmDialogVariant = "default" | "danger";


export function ConfirmDialog({
  open, title, body, confirmLabel = "Confirm",
  cancelLabel = "Cancel", variant = "default",
  onConfirm, onCancel, testId = "confirm-dialog",
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  onConfirm: () => void;
  onCancel: () => void;
  testId?: string;
}) {
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  // Focus the confirm button when the dialog opens so Enter triggers
  // the action — mirrors browser-native confirm() ergonomics.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => confirmRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // Esc cancels.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmClass = variant === "danger"
    ? "bg-ember text-text-inverse hover:opacity-90"
    : "bg-teal text-text-inverse hover:opacity-90";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4"
      onClick={(e) => {
        // Click on the dim layer (not the dialog body) cancels.
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
        aria-describedby={`${testId}-body`}
        data-testid={testId}
        className="flex w-full max-w-md flex-col rounded-lg border border-border-subtle bg-canvas shadow-xl"
      >
        <header className="border-b border-border-subtle px-5 py-4">
          <h2
            id={`${testId}-title`}
            className="text-base font-semibold text-text-primary"
          >
            {title}
          </h2>
        </header>
        <div className="px-5 py-4 text-sm text-text-secondary">
          <p id={`${testId}-body`}>{body}</p>
        </div>
        <footer className="flex justify-end gap-2 border-t border-border-subtle px-5 py-4">
          <button
            type="button"
            data-testid={`${testId}-cancel`}
            onClick={onCancel}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            data-testid={`${testId}-confirm`}
            onClick={onConfirm}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </footer>
      </section>
    </div>
  );
}
